/**
 * Helpers for the Vue 3 port of the CoreUI v3 (Vue 2) components.
 * In Vue 3 `$attrs` also carries `class`, `style` and `on*` listeners, so components that used
 * `inheritAttrs: false` + `v-bind="$attrs"` (+ `v-on="$listeners"`) need them split again.
 */
let uid = 0
export function makeUid () {
  uid += 1
  return 'uid-' + Math.random().toString(36).substr(2, 6) + uid
}

export function pickByKeys (obj, keys) {
  return keys.reduce((out, k) => {
    if (Object.prototype.hasOwnProperty.call(obj, k)) out[k] = obj[k]
    return out
  }, {})
}

const isListener = (k) => /^on[A-Z]/.test(k)

/** class/style that Vue 2 always put on the component root */
export function rootAttrs (attrs) {
  const out = {}
  if (attrs.class !== undefined) out.class = attrs.class
  if (attrs.style !== undefined) out.style = attrs.style
  return out
}

/** everything except class/style (Vue 2 `$attrs` + `$listeners`) */
export function innerAttrs (attrs, { listeners = true } = {}) {
  const out = {}
  Object.keys(attrs).forEach((k) => {
    if (k === 'class' || k === 'style') return
    if (!listeners && isListener(k)) return
    out[k] = attrs[k]
  })
  return out
}

/** only `on*` listeners (Vue 2 `$listeners`) */
export function listenersOf (attrs) {
  const out = {}
  Object.keys(attrs).forEach((k) => { if (isListener(k)) out[k] = attrs[k] })
  return out
}

export function deepClone (value) {
  if (Array.isArray(value)) return value.map(deepClone)
  if (value && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype) {
    const out = {}
    Object.keys(value).forEach((k) => { out[k] = deepClone(value[k]) })
    return out
  }
  return value
}

/** Flatten fragments / arrays of vnodes (used where Vue 2 code walked `$children`). */
export function flattenVNodes (nodes, out = []) {
  (nodes || []).forEach((n) => {
    if (Array.isArray(n)) flattenVNodes(n, out)
    else if (n && typeof n.type === 'symbol' && Array.isArray(n.children)) flattenVNodes(n.children, out)
    else if (n) out.push(n)
  })
  return out
}
